import {
  Box,
  Card,
  Divider,
  FormControl,
  Grid,
  Link,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import ValidationList from 'shared/ValidationList'
import { useAuthStore } from 'store'
import { PropertyAdBasicInfoDTO } from 'types'

import { ReactComponent as SpourgitiSvg } from 'assets/svg/icons/lists/g-spourgiti.svg'
import { ReactComponent as CircledDotsSvg } from 'assets/svg/new-icons/circled-dots.svg'
import { ReactComponent as TrashcanSvg } from 'assets/svg/new-icons/trashcan.svg'
import {
  PropertyAdStatusEnum,
  PropertyAdSubCategoryEnum,
  PropertyAdTypeEnum,
} from 'types/enums'

import PropertyInfoBox from './PropertyInfoBox'
import { CustomSwitch } from '.'

interface Props {
  propAd: PropertyAdBasicInfoDTO
  handleChangeActiveStatus: (arg: PropertyAdBasicInfoDTO) => void
  handleOpenDeleteDialog: (arg: PropertyAdBasicInfoDTO) => void
}

function PropertyCardMobile(props: Props) {
  const { propAd, handleChangeActiveStatus, handleOpenDeleteDialog } = props

  const { t } = useTranslation()

  const currentUser = useAuthStore((state) => state.user)

  const CURRENT_USER_IS_ADMIN = currentUser?.role === 'ADMIN'

  return (
    <Card
      sx={{
        boxShadow: `3px 3px 8px 0px #0000004D`,
        border: '0.5px solid #A9A9A9',
        borderRadius: '8px',
        mx: { xs: '11px' },
      }}
    >
      <Grid container direction={'column'} spacing={0}>
        <Grid item xs>
          <Grid container alignItems={'center'} sx={{ py: '5px' }}>
            <Grid item xs={3} sx={{ display: 'grid', placeItems: 'center' }}>
              <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.paper',
                      color: 'black',
                      fontSize: '15px',
                      border: (theme) =>
                        `1px solid ${theme.palette.primary.main}`,
                    },
                  },
                }}
                placement="right"
                title={
                  propAd.propertyAdStatus === PropertyAdStatusEnum.Invalid ? (
                    <ValidationList options={propAd?.validationMessages} />
                  ) : (
                    ''
                  )
                }
              >
                <FormControl variant="standard" sx={{}}>
                  <CustomSwitch
                    status={propAd.propertyAdStatus}
                    disabled={[PropertyAdStatusEnum.Invalid].includes(
                      propAd.propertyAdStatus
                    )}
                    checked={[PropertyAdStatusEnum.Active].includes(
                      propAd.propertyAdStatus
                    )}
                    onChange={() => handleChangeActiveStatus(propAd)}
                  />
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item xs={3}>
              <PropertyInfoBox
                tooltip={propAd.externalId}
                value={propAd.externalId}
                label={t('tables.propertyAds.columns.name')}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={400} fontSize="14px" textAlign={'center'}>
                {' '}
                {t(
                  `tables.propertyAds.categoryType.${propAd?.propertyAdCategoryType
                    .toString()
                    .toLowerCase()}`
                )}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={400} fontSize="14px" textAlign={'center'}>
                {propAd.salesPrice != null
                  ? t('generics.currency', {
                      count: Number(propAd.salesPrice.toFixed(2)),
                    })
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Grid item container direction={'row'}>
          <Grid item xs sx={{ py: '10px' }}>
            <Typography
              fontWeight={400}
              fontSize="14px"
              textAlign="center"
              sx={{ width: '100%' }}
            >
              {propAd?.location?.replaceAll(' /', ',')}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid item container direction={'row'} sx={{ py: '10px' }}>
          <Grid item xs={4}>
            <Typography
              fontWeight={400}
              fontSize="14px"
              textAlign="center"
              sx={{ width: '100%' }}
            >
              {propAd.propertyAdType !== PropertyAdTypeEnum.UNKNOWN &&
              propAd.propertySubCategory !== PropertyAdSubCategoryEnum.Unknown
                ? t(
                    `enums.ESTATE_PROPERTY_${
                      propAd.propertyAdType !== PropertyAdTypeEnum.PROPERTY
                        ? propAd.propertyAdType + '_'
                        : ''
                    }TYPE.${propAd.propertySubCategory.toLowerCase()}.base`
                  )
                : t('tables.propertyAds.adType.unknown')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              fontWeight={400}
              fontSize="14px"
              textAlign="center"
              sx={{ width: '100%' }}
            >
              {t(
                `tables.propertyAds.adType.${propAd?.propertyAdType?.toLowerCase()}`
              )}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography
              fontWeight={400}
              fontSize="14px"
              textAlign="center"
              sx={{ width: '100%' }}
            >
              {propAd.squareMeters != null
                ? t('generics.area', {
                    count: Number(propAd.squareMeters.toFixed(2)),
                  })
                : '-'}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid item container direction={'row'} sx={{ py: '5px' }} spacing={0}>
          <Grid item xs={4}>
            <PropertyInfoBox
              tooltip=""
              value={propAd.weeklyViews.toString()}
              label={t('tables.propertyAds.columns.weeklyViews.short')}
            />
          </Grid>
          <Grid item xs={4}>
            <PropertyInfoBox
              tooltip=""
              value={propAd.views.toString()}
              label={t('tables.propertyAds.columns.views')}
            />
          </Grid>
          <Grid item xs={4}>
            <Box
              display={'flex'}
              justifyContent="space-evenly"
              alignItems={'baseline'}
            >
              {propAd.propertyAdStatus === PropertyAdStatusEnum.Active ? (
                <Tooltip
                  title={
                    t(
                      'tables.propertyAds.columns.actions.view'
                    ) as unknown as string
                  }
                >
                  <Link
                    href={`${process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}property-ads/el:${propAd?.alternativeId}`}
                    underline="none"
                    target="_blank"
                  >
                    <SvgIcon
                      component={SpourgitiSvg}
                      inheritViewBox
                      sx={{
                        position: 'relative',
                        top: '4px',
                        color: 'black',
                        width: '26px',
                        height: 'auto',
                      }}
                    />
                  </Link>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    t(
                      'tables.propertyAds.columns.actions.inactiveProperty'
                    ) as unknown as string
                  }
                >
                  <SvgIcon
                    component={SpourgitiSvg}
                    inheritViewBox
                    sx={{
                      color: 'black',
                      width: '26px',
                      height: 'auto',
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip
                title={
                  (t(
                    'tables.propertyAds.columns.actions.edit'
                  ) as unknown as string) ?? ''
                }
              >
                <Link
                  component={RouterLink}
                  to={
                    !CURRENT_USER_IS_ADMIN
                      ? `${propAd?.id}`
                      : `/property-ads/${propAd?.id}`
                  }
                  underline="none"
                >
                  <SvgIcon
                    component={CircledDotsSvg}
                    inheritViewBox
                    sx={{
                      fill: 'white',
                      width: '30px',
                      height: 'auto',
                    }}
                  />
                </Link>
              </Tooltip>
              <Tooltip
                title={
                  (t(
                    'tables.propertyAds.columns.actions.delete'
                  ) as unknown as string) ?? ''
                }
              >
                <SvgIcon
                  component={TrashcanSvg}
                  inheritViewBox
                  onClick={() => handleOpenDeleteDialog(propAd)}
                  sx={{
                    fill: 'white',
                    width: '20px',
                    height: 'auto',
                  }}
                />
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}

export default PropertyCardMobile
